export default function AppAdSection() {
  return (
    <section className="w-full bg-primary-900 py-16 md:py-24 overflow-hidden">
      <div className="container-content">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
          {/* Left — Phone mockup */}
          <div className="lg:w-[45%] flex justify-center">
            <div className="relative">
              {/* Phone frame */}
              <div className="w-[260px] md:w-[300px] bg-foreground-950 rounded-[2.5rem] p-2 shadow-2xl">
                <div className="bg-background-50 rounded-[2rem] overflow-hidden">
                  {/* Notch */}
                  <div className="flex justify-center pt-3 pb-2">
                    <div className="w-20 h-5 bg-foreground-950 rounded-full" />
                  </div>
                  {/* App screen content */}
                  <div className="px-4 pb-6 space-y-4">
                    <div className="flex items-center gap-2 mb-3">
                      <img
                        src="/cleanup_icon.png"
                        alt="CleanUp Naija"
                        className="w-8 h-8 object-contain"
                        onError={(e) => {
                          const target = e.currentTarget;
                          target.style.display = 'none';
                          target.parentElement!.querySelector('.appad-logo-fallback')!.classList.remove('hidden');
                        }}
                      />
                      <div className="appad-logo-fallback hidden w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                        <i className="ri-sparkling-2-line text-sm" />
                      </div>
                      <span className="font-heading font-bold text-sm text-foreground-950">CleanUp Naija</span>
                    </div>
                    <div className="space-y-2">
                      <div className="h-16 bg-primary-50 rounded-lg flex items-center gap-3 px-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-home-smile-line text-primary-600 text-sm" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground-950">Home Cleaning</p>
                        </div>
                      </div>
                      <div className="h-16 bg-background-100 rounded-lg flex items-center gap-3 px-3">
                        <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                          <i className="ri-building-line text-secondary-600 text-sm" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground-950">Industrial</p>
                        </div>
                      </div>
                      <div className="h-16 bg-background-100 rounded-lg flex items-center gap-3 px-3">
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                          <i className="ri-recycle-line text-primary-600 text-sm" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground-950">Waste Pickup</p>
                        </div>
                      </div>
                      <div className="h-16 bg-background-100 rounded-lg flex items-center gap-3 px-3">
                        <div className="w-8 h-8 rounded-full bg-secondary-100 flex items-center justify-center">
                          <i className="ri-car-line text-secondary-600 text-sm" />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-foreground-950">Car Washing</p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-primary-500 text-background-50 text-center py-2.5 rounded-lg text-xs font-semibold">
                      Book Now
                    </div>
                  </div>
                </div>
              </div>
              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-16 h-16 bg-secondary-500/20 rounded-full blur-xl" />
              <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-primary-500/20 rounded-full blur-xl" />
            </div>
          </div>

          {/* Right — Content */}
          <div className="lg:w-[55%]">
            <p className="font-label font-medium text-label-lg text-secondary-400 tracking-wide uppercase mb-3">
              Download the App
            </p>
            <h2 className="font-heading text-display-md text-background-50 mb-4 text-balance">
              Book cleanups in seconds, track your crew in real time.
            </h2>
            <p className="text-body-lg text-background-300 leading-relaxed mb-8">
              The CleanUp Naija app puts professional cleaning at your fingertips. Browse services, schedule appointments, chat with your assigned crew, and pay securely — all from your phone.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { icon: 'ri-calendar-check-line', title: 'Instant Booking', desc: 'Pick a time, confirm in seconds' },
                { icon: 'ri-map-pin-user-line', title: 'Live Tracking', desc: 'See when your crew is en route' },
                { icon: 'ri-secure-payment-line', title: 'Secure Payments', desc: 'Pay with card, transfer, or USSD' },
                { icon: 'ri-star-smile-line', title: 'Rate & Rebook', desc: 'Save favourite crews for next time' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-800 shrink-0">
                    <i className={`${item.icon} text-secondary-400 text-lg`} />
                  </div>
                  <div>
                    <p className="font-semibold text-body-sm text-background-100">{item.title}</p>
                    <p className="text-body-xs text-background-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-foreground-950 hover:bg-background-100 transition-colors">
                <i className="ri-apple-fill text-xl" />
                <div className="text-left">
                  <p className="text-[10px] leading-none text-foreground-500">Download on the</p>
                  <p className="text-sm font-semibold leading-tight">App Store</p>
                </div>
              </button>
              <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-background-50 text-foreground-950 hover:bg-background-100 transition-colors">
                <i className="ri-google-play-fill text-xl" />
                <div className="text-left">
                  <p className="text-[10px] leading-none text-foreground-500">Get it on</p>
                  <p className="text-sm font-semibold leading-tight">Google Play</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}